import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useBlocker, type BlockerFunction } from "react-router-dom";
import { useConfirm, type ConfirmOptions } from "../components/ui/useConfirm";

export interface UnsavedChangesGuardOptions extends ConfirmOptions {
  when: boolean;
}

type GuardRegistration = Omit<UnsavedChangesGuardOptions, "when">;

interface UnsavedChangesContextValue {
  register: (source: symbol, guard: GuardRegistration | null) => void;
  navigateUnguarded: (navigate: () => void) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

function isDifferentLocation(
  currentLocation: { pathname: string; search: string; hash: string },
  nextLocation: { pathname: string; search: string; hash: string },
) {
  return (
    currentLocation.pathname !== nextLocation.pathname ||
    currentLocation.search !== nextLocation.search ||
    currentLocation.hash !== nextLocation.hash
  );
}

/**
 * Owns the single app-wide leave prompt for registered dirty workspaces.
 * It covers links, programmatic navigation and browser history; a native
 * beforeunload prompt remains the fail-safe for reloads and closing the tab.
 */
export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const [registrations, setRegistrations] = useState<Map<symbol, GuardRegistration>>(
    () => new Map(),
  );
  const { confirm, confirmDialog } = useConfirm();
  const promptOpenRef = useRef(false);
  const navigationTriggerRef = useRef<HTMLElement | null>(null);
  const unguardedRef = useRef(false);

  const register = useCallback((source: symbol, guard: GuardRegistration | null) => {
    setRegistrations((current) => {
      const next = new Map(current);
      if (guard) next.set(source, guard);
      else next.delete(source);
      return next;
    });
  }, []);

  const activeGuard = useMemo(
    () => Array.from(registrations.values()).at(-1) ?? null,
    [registrations],
  );

  // React Router evaluates blockers synchronously inside navigate(), so the flag
  // only covers the navigation started by the callback itself.
  const navigateUnguarded = useCallback((navigate: () => void) => {
    unguardedRef.current = true;
    try {
      navigate();
    } finally {
      unguardedRef.current = false;
    }
  }, []);

  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      if (unguardedRef.current) return false;
      const blocks = Boolean(activeGuard) && isDifferentLocation(currentLocation, nextLocation);
      if (blocks && document.activeElement instanceof HTMLElement) {
        navigationTriggerRef.current = document.activeElement;
      }
      return blocks;
    },
    [activeGuard],
  );
  const blocker = useBlocker(shouldBlock);

  useEffect(() => {
    if (!activeGuard) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [activeGuard]);

  useEffect(() => {
    if (blocker.state !== "blocked" || promptOpenRef.current) return;

    if (!activeGuard) {
      blocker.proceed();
      return;
    }

    promptOpenRef.current = true;
    void confirm(activeGuard).then((shouldLeave) => {
      promptOpenRef.current = false;
      if (shouldLeave) {
        navigationTriggerRef.current = null;
        blocker.proceed();
        return;
      }

      const navigationTrigger = navigationTriggerRef.current;
      navigationTriggerRef.current = null;
      blocker.reset();
      window.setTimeout(() => {
        if (navigationTrigger?.isConnected) navigationTrigger.focus();
      }, 0);
    });
  }, [activeGuard, blocker, confirm]);

  const value = useMemo(() => ({ register, navigateUnguarded }), [navigateUnguarded, register]);

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      {confirmDialog}
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChangesGuard({
  when,
  title,
  body,
  confirmLabel,
  cancelLabel,
  variant,
}: UnsavedChangesGuardOptions) {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error("useUnsavedChangesGuard must be used inside UnsavedChangesProvider");
  }

  const sourceRef = useRef(Symbol("unsaved-changes-source"));

  useEffect(() => {
    const source = sourceRef.current;
    context.register(
      source,
      when ? { title, body, confirmLabel, cancelLabel, variant } : null,
    );
    return () => context.register(source, null);
  }, [body, cancelLabel, confirmLabel, context, title, variant, when]);
}

/**
 * Runs a navigation that only mirrors state already on screen into the URL,
 * e.g. the selected record of the current workspace. It never passes the dirty
 * guard, so it can neither prompt nor be held back. Leaving a workspace must
 * keep using regular navigation.
 */
export function useUnguardedNavigation() {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error("useUnguardedNavigation must be used inside UnsavedChangesProvider");
  }
  return context.navigateUnguarded;
}
